import React, { useCallback, useEffect, useState } from "react"
import BackOfficeAPIClient from "../../clients/BackofficeAPIClient"
import { SupplierRegistry } from "../../enums/SupplierRegistry"

interface RegistryOption {
    value: SupplierRegistry
    label: string
    description: string
    unavailableReason: string
}


const ApprovisionnementVN: React.FC = () => {
    const backofficeApiClient = new BackOfficeAPIClient()

    const [ registry, setRegistry ] = useState<SupplierRegistry | null>(null)
    const [ savedRegistry, setSavedRegistry ] = useState<SupplierRegistry | null>(null)
    const [ dbsConfigured, setDbsConfigured ] = useState<boolean>(false)
    const [ loading, setLoading ] = useState<boolean>(true)
    const [ saving, setSaving ] = useState<boolean>(false)
    const [ error, setError ] = useState<string>("")

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true)

            const [ setting, status ] = await Promise.all([
                backofficeApiClient.get<{ value: SupplierRegistry | null }>("/group-settings/supplier_registry"),
                backofficeApiClient.get<{ configured: boolean }>("/interfaces/dbs/config/status"),
            ])

            setRegistry(setting.value)
            setSavedRegistry(setting.value)
            setDbsConfigured(status.configured)
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchAll()
    }, [ fetchAll ])

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        if (registry === null) {
            return
        }

        setSaving(true)
        setError("")

        try {
            await backofficeApiClient.put("/group-settings/supplier_registry", { value: registry })
            setSavedRegistry(registry)
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setSaving(false)
        }
    }

    const options: RegistryOption[] = [
        {
            value: SupplierRegistry.EDV,
            label: "Référentiel eDataVoice",
            description: "Gestion manuelle des fournisseurs",
            unavailableReason: "Le référentiel est indisponible pour le moment",
        },
        {
            value: SupplierRegistry.DBS,
            label: "Référentiel DBS",
            description: "Récupération des fournisseurs paramétrés dans DBS",
            unavailableReason: dbsConfigured ? "" : "Identifiants non renseignés",
        },
    ]

    const hasChanges: boolean = registry !== savedRegistry

    if (loading) {
        return (
            <div className="container-fluid mb-2">
                <h2>Approvisionnement VN</h2>
                <p><i className="fa fa-spinner fa-spin fa-fw"></i> Chargement...</p>
            </div>
        )
    }

    return (
        <div className="container-fluid mb-2">
            <h2 className="mb-4">Approvisionnement VN</h2>

            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button type="button" className="nav-link btn btn-link active">
                        Référentiel Source
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className="nav-link btn btn-link disabled"
                        disabled
                        title="Disponible prochainement"
                    >
                        Alertes
                    </button>
                </li>
            </ul>

            {error !== "" &&
                <div className="alert alert-danger">{error}</div>
            }

            <form onSubmit={handleSubmit}>
                <h6 className="mb-3">Choix du référentiel fournisseur source</h6>

                <div className="d-flex flex-wrap">
                    {options.map((option) => {
                        const disabled: boolean = option.unavailableReason !== ""
                        const selected: boolean = registry === option.value

                        return (
                            <label
                                key={option.value}
                                className={`card mr-3 mb-3${selected ? " border-primary" : ""}`}
                                style={{ width: "22rem", cursor: disabled ? "not-allowed" : "pointer" }}
                                title={option.unavailableReason}
                            >
                                <div className="card-body p-3 d-flex align-items-center">
                                    <input
                                        type="radio"
                                        name="supplier_registry"
                                        id={`supplier_registry_${option.value}`}
                                        className="mr-3"
                                        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                                        value={option.value}
                                        checked={selected}
                                        disabled={disabled}
                                        onChange={() => setRegistry(option.value)}
                                    />
                                    <div>
                                        <div className={selected ? "text-primary" : (disabled ? "text-muted" : "")}>
                                            {option.label}
                                        </div>
                                        <small className="text-muted">{option.description}</small>
                                        {disabled &&
                                            <div>
                                                <small className="text-muted font-italic">{option.unavailableReason}</small>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </label>
                        )
                    })}
                </div>

                <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!hasChanges || saving || registry === null}
                >
                    {saving ? "Enregistrement..." : "Valider"}
                </button>
            </form>
        </div>
    )
}

export default ApprovisionnementVN
